// apps/web/src/store/slices/authSlice.js

import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit'

import { AUTH_TOKEN } from 'constants/AuthConstant'
import AuthService from 'services/AuthService'

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  redirect: '',

  token:
    localStorage.getItem(AUTH_TOKEN) ||
    localStorage.getItem('AUTH_TOKEN') ||
    localStorage.getItem('ACCESS_TOKEN') ||
    null,
}

/**
 * Define a primeira página que o usuário pode acessar.
 *
 * Isso evita enviar um operador para o Dashboard quando ele
 * possui acesso somente ao Estoque, por exemplo.
 */
const getFirstAllowedRoute = (
  permissions = [],
  role = '',
) => {
  if (role === 'ADMIN_NLB') {
    return '/app/roulette/dashboard'
  }

  const routes = [
    {
      permission: 'dashboard.view',
      path: '/app/roulette/dashboard',
    },
    {
      permission: 'clients.view',
      path: '/app/my-company',
    },
    {
      permission: 'users.view',
      path: '/app/roulette/client-users',
    },
    {
      permission: 'forms.view',
      path: '/app/lead-forms',
    },
    {
      permission: 'leads.view',
      path: '/app/leads',
    },
    {
      permission: 'events.view',
      path: '/app/roulette/events',
    },
    {
      permission: 'games.view',
      path: '/app/roulette/games',
    },
    {
      permission: 'prizes.view',
      path: '/app/roulette/prizes',
    },
    {
      permission: 'inventory.view',
      path: '/app/roulette/inventory',
    },
    {
      permission: 'daily-limits.view',
      path: '/app/roulette/daily-limits',
    },
    {
      permission: 'spins.view',
      path: '/app/roulette/spins',
    },
    {
      permission: 'reports.view',
      path: '/app/roulette/reports',
    },
    {
      permission: 'settings.view',
      path: '/app/roulette/settings',
    },
  ]

  const normalizedPermissions =
    Array.isArray(permissions)
      ? permissions
      : []

  const firstAllowedRoute =
    routes.find((route) =>
      normalizedPermissions.includes(
        route.permission,
      ),
    )

  return (
    firstAllowedRoute?.path ||
    '/auth/login'
  )
}

/**
 * Salva todos os dados retornados pelo login.
 */
const saveLoginData = (response) => {
  const token =
    response?.token ||
    response?.accessToken

  if (!token) {
    throw new Error(
      'Token não retornado pela API',
    )
  }

  const role =
    response?.user?.role || ''

  const permissions =
    response?.permissions ||
    response?.user?.permissions ||
    []

  const normalizedPermissions =
    Array.isArray(permissions)
      ? permissions
      : []

  const clients =
    Array.isArray(response?.clients)
      ? response.clients
      : []

  localStorage.setItem(
    AUTH_TOKEN,
    token,
  )

  localStorage.setItem(
    'AUTH_TOKEN',
    token,
  )

  localStorage.setItem(
    'ACCESS_TOKEN',
    token,
  )

  if (response?.user) {
    localStorage.setItem(
      'AUTH_USER',
      JSON.stringify({
        ...response.user,
        permissions:
          normalizedPermissions,
      }),
    )
  } else {
    localStorage.removeItem(
      'AUTH_USER',
    )
  }

  localStorage.setItem(
    'USER_ROLE',
    role,
  )

  localStorage.setItem(
    'CLIENT_ID',
    response?.clientId || '',
  )

  localStorage.setItem(
    'CLIENTS',
    JSON.stringify(clients),
  )

  localStorage.setItem(
    'USER_PERMISSIONS',
    JSON.stringify(
      normalizedPermissions,
    ),
  )

  localStorage.setItem(
    'CLIENT_USER_ROLE',
    response?.clientUserRole || '',
  )

  return {
    token,

    redirectPath:
      getFirstAllowedRoute(
        normalizedPermissions,
        role,
      ),
  }
}

/**
 * Remove completamente os dados da sessão anterior.
 */
const clearLoginData = () => {
  localStorage.removeItem(
    AUTH_TOKEN,
  )

  localStorage.removeItem(
    'AUTH_TOKEN',
  )

  localStorage.removeItem(
    'ACCESS_TOKEN',
  )

  localStorage.removeItem(
    'AUTH_USER',
  )

  localStorage.removeItem(
    'USER_ROLE',
  )

  localStorage.removeItem(
    'CLIENT_ID',
  )

  localStorage.removeItem(
    'CLIENTS',
  )

  localStorage.removeItem(
    'USER_PERMISSIONS',
  )

  localStorage.removeItem(
    'CLIENT_USER_ROLE',
  )
}

/**
 * Salva os dados e recarrega a aplicação.
 *
 * O reload completo é necessário porque o NavigationConfig.js
 * é montado quando os módulos do React são carregados.
 */
const completeLogin = (response) => {
  const loginData =
    saveLoginData(response)

  window.setTimeout(() => {
    window.location.replace(
      loginData.redirectPath,
    )
  }, 50)

  return loginData.token
}

export const signIn =
  createAsyncThunk(
    'auth/login',

    async (
      data,
      {
        rejectWithValue,
      },
    ) => {
      try {
        const response =
          await AuthService.login({
            email: data.email,
            password: data.password,
          })

        console.log(
          'LOGIN RESPONSE:',
          response,
        )

        return completeLogin(
          response,
        )
      } catch (err) {
        console.error(
          'ERRO NO LOGIN:',
          err,
        )

        return rejectWithValue(
          err?.response?.data?.message ||
            err?.message ||
            'Erro ao fazer login',
        )
      }
    },
  )

export const signUp =
  createAsyncThunk(
    'auth/register',

    async (
      data,
      {
        rejectWithValue,
      },
    ) => {
      try {
        const response =
          await AuthService.register(
            data,
          )

        return completeLogin(
          response,
        )
      } catch (err) {
        console.error(
          'ERRO NO CADASTRO:',
          err,
        )

        return rejectWithValue(
          err?.response?.data?.message ||
            err?.message ||
            'Erro ao cadastrar',
        )
      }
    },
  )

export const signOut =
  createAsyncThunk(
    'auth/logout',

    async () => {
      try {
        await AuthService.logout()
      } catch (error) {
        console.warn(
          'Não foi possível concluir o logout na API:',
          error,
        )
      }

      clearLoginData()

      window.setTimeout(() => {
        window.location.replace(
          '/auth/login',
        )
      }, 50)

      return true
    },
  )

export const signInWithGoogle =
  createAsyncThunk(
    'auth/signInWithGoogle',

    async (
      _,
      {
        rejectWithValue,
      },
    ) => {
      try {
        const response =
          await AuthService.loginInOAuth()

        return completeLogin(
          response,
        )
      } catch (err) {
        console.error(
          'ERRO NO LOGIN GOOGLE:',
          err,
        )

        return rejectWithValue(
          err?.response?.data?.message ||
            err?.message ||
            'Erro ao entrar com Google',
        )
      }
    },
  )

export const signInWithFacebook =
  createAsyncThunk(
    'auth/signInWithFacebook',

    async (
      _,
      {
        rejectWithValue,
      },
    ) => {
      try {
        const response =
          await AuthService.loginInOAuth()

        return completeLogin(
          response,
        )
      } catch (err) {
        console.error(
          'ERRO NO LOGIN FACEBOOK:',
          err,
        )

        return rejectWithValue(
          err?.response?.data?.message ||
            err?.message ||
            'Erro ao entrar com Facebook',
        )
      }
    },
  )

export const authSlice =
  createSlice({
    name: 'auth',

    initialState,

    reducers: {
      authenticated: (
        state,
        action,
      ) => {
        state.loading = false
        state.redirect = '/'
        state.token =
          action.payload
      },

      showAuthMessage: (
        state,
        action,
      ) => {
        state.message =
          action.payload

        state.showMessage =
          true

        state.loading =
          false
      },

      hideAuthMessage: (
        state,
      ) => {
        state.message = ''
        state.showMessage =
          false
      },

      signOutSuccess: (
        state,
      ) => {
        state.loading = false
        state.token = null
        state.redirect = '/'

        clearLoginData()
      },

      showLoading: (
        state,
      ) => {
        state.loading = true
      },

      signInSuccess: (
        state,
        action,
      ) => {
        state.loading = false
        state.token =
          action.payload
      },
    },

    extraReducers: (
      builder,
    ) => {
      builder
        .addCase(
          signIn.pending,
          (state) => {
            state.loading = true
            state.message = ''
            state.showMessage = false
          },
        )

        .addCase(
          signIn.fulfilled,
          (
            state,
            action,
          ) => {
            state.loading = false
            state.redirect = '/'
            state.token =
              action.payload
          },
        )

        .addCase(
          signIn.rejected,
          (
            state,
            action,
          ) => {
            state.message =
              action.payload

            state.showMessage =
              true

            state.loading =
              false
          },
        )

        .addCase(
          signOut.pending,
          (state) => {
            state.loading = true
          },
        )

        .addCase(
          signOut.fulfilled,
          (state) => {
            state.loading = false
            state.token = null
            state.redirect = '/'
          },
        )

        .addCase(
          signOut.rejected,
          (state) => {
            state.loading = false
            state.token = null
            state.redirect = '/'

            clearLoginData()
          },
        )

        .addCase(
          signUp.pending,
          (state) => {
            state.loading = true
            state.message = ''
            state.showMessage = false
          },
        )

        .addCase(
          signUp.fulfilled,
          (
            state,
            action,
          ) => {
            state.loading = false
            state.redirect = '/'
            state.token =
              action.payload
          },
        )

        .addCase(
          signUp.rejected,
          (
            state,
            action,
          ) => {
            state.message =
              action.payload

            state.showMessage =
              true

            state.loading =
              false
          },
        )

        .addCase(
          signInWithGoogle.pending,
          (state) => {
            state.loading = true
            state.message = ''
            state.showMessage = false
          },
        )

        .addCase(
          signInWithGoogle.fulfilled,
          (
            state,
            action,
          ) => {
            state.loading = false
            state.redirect = '/'
            state.token =
              action.payload
          },
        )

        .addCase(
          signInWithGoogle.rejected,
          (
            state,
            action,
          ) => {
            state.message =
              action.payload

            state.showMessage =
              true

            state.loading =
              false
          },
        )

        .addCase(
          signInWithFacebook.pending,
          (state) => {
            state.loading = true
            state.message = ''
            state.showMessage = false
          },
        )

        .addCase(
          signInWithFacebook.fulfilled,
          (
            state,
            action,
          ) => {
            state.loading = false
            state.redirect = '/'
            state.token =
              action.payload
          },
        )

        .addCase(
          signInWithFacebook.rejected,
          (
            state,
            action,
          ) => {
            state.message =
              action.payload

            state.showMessage =
              true

            state.loading =
              false
          },
        )
    },
  })

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
} = authSlice.actions

export default authSlice.reducer